import { RankKit } from "@/components/site/RankKit";
import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import {
  TableOfContents,
  Section,
  SectionHeader,
  CTABand,
  FAQ,
  StatRow,
  Breadcrumbs,
  InlineCTA,
  FeatureGrid,
} from "@/components/site/Blocks";
import { TLDR, ProsCons, ExpertInsight } from "@/components/site/SeoBlocks";
import { HelocPricingTool } from "@/components/heloc/HelocPricingTool";
import { DscrProgramLinks } from "@/components/site/DscrProgramLinks";
import { DscrCrossProductLinks } from "@/components/site/DscrCrossProductLinks";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import { getDscrClusterHero } from "@/data/dscr-cluster-heroes";


const URL = "/dscr-second-lien-heloc";

const KEYWORDS = [
  "DSCR second lien HELOC",
  "DSCR 2nd lien HELOC",
  "investment property HELOC second lien",
  "rental property second lien HELOC",
  "second position HELOC investment property",
  "non-owner occupied second lien HELOC",
  "second lien home equity line rental property",
  "CLTV second lien investment property",
].join(", ");

const FAQS = [
  {
    q: "What is a DSCR second-lien HELOC?",
    a: "It is a home equity line of credit recorded in second position against a non-owner-occupied investment property. Your existing first mortgage stays in place where the program rules and the first lienholder's terms permit, and the new line sits behind it.",
  },
  {
    q: "How is CLTV calculated on a 2nd lien HELOC?",
    a: "Combined loan-to-value is (existing first-lien balance + requested HELOC amount) ÷ property value. That is exactly the calculation the pricing tool on this page uses to match your scenario to a CLTV band.",
  },
  {
    q: "Does my first mortgage have to be paid off?",
    a: "No. A second-position line is designed to leave the first mortgage untouched, which is the point for investors holding a below-market first-lien rate. Subordination or lienholder consent requirements can apply.",
  },
  {
    q: "Is a second-lien HELOC available in every state?",
    a: "No. Property state is required and only states where this business-purpose program is currently offered can be priced. If a state is not offered, the tool returns no pricing.",
  },
  {
    q: "Why does the tool not show an APR?",
    a: "This tool shows the note rate, origination fee percentage and dollars, and estimated principal and interest separately. The origination fee is not added into the note rate. Scenario-specific cost detail is provided with your review.",
  },
  {
    q: "Can the funds be used for anything?",
    a: "Business purposes only — acquisitions, rehab, reserves, or business debt on investment property. Owner-occupied and personal, family, or household use is not supported.",
  },
  {
    q: "Can I use a second-lien HELOC to buy another rental property?",
    a: "Yes. Draws are commonly used as down payment or all-cash acquisition funds for a new investment property, since the use of proceeds is business purpose. Combine it with the acquisition's own financing to structure the deal.",
  },
  {
    q: "What happens to the second lien if I sell the property?",
    a: "The second-lien balance is paid off from sale proceeds at closing, in line behind the first mortgage, just like any other recorded lien. Both liens must be satisfied, or otherwise addressed, before clear title transfers.",
  },
  {
    q: "Does the second-lien HELOC require its own appraisal?",
    a: "Typically yes, a current valuation is required to establish the combined loan-to-value the pricing tool uses. The valuation method used can vary by loan amount and CLTV band.",
  },
  {
    q: "Is interest-only draw structure available on a second-lien HELOC?",
    a: "Structure varies by program and CLTV band and is disclosed in your scenario-specific terms rather than promised uniformly here. Confirm the draw and repayment structure for your exact scenario before relying on it.",
  },
  {
    q: "How does DSCR get calculated when there are two liens on the property?",
    a: "Debt service for DSCR purposes generally includes the combined payment obligation across both the first lien and the new second-lien line, weighed against the property's qualifying rent. This is why CLTV and combined payment matter more than the second lien in isolation.",
  },
  {
    q: "Can an LLC hold title and take out the second-lien HELOC?",
    a: "Yes. Business-purpose second-lien financing is commonly closed in the name of an LLC or other entity that holds title to the investment property, subject to standard entity documentation review. Personal, owner-occupied use is not eligible under this program.",
  },
];

export const Route = createFileRoute("/dscr-second-lien-heloc")({
  head: () =>
    buildHead({
      title: "DSCR Second-Lien HELOC — Investment Property 2nd Lien",
      description:
        "A second-position HELOC on a rental: keep your existing first mortgage, price by FICO, term and combined LTV. Business-purpose, non-owner-occupied only.",
      url: URL,
      keywords: KEYWORDS,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "DSCR Loans", url: "/dscr-loans" },
        { name: "Investment Property HELOC", url: "/dscr-heloc" },
        { name: "Second-Lien HELOC", url: URL },
      ],
      service: { name: "Investment Property Second-Lien HELOC" },
      faqs: FAQS,
    }),
  component: SecondLienHeloc,
});

function SecondLienHeloc() {
  return (
    <SiteShell hideBreadcrumbs productLabel="Investor HELOC">
      <PageHero
        eyebrow="DSCR · Second-position equity line"
        title="DSCR second-lien HELOC for investment property"
        subtitle="A 2nd lien HELOC recorded behind the first mortgage you already have on a non-owner-occupied rental. Draw against equity without disturbing your existing first-lien rate, and price the scenario below by FICO band, term, and combined loan-to-value."
        primaryHref="/apply?product=heloc"
        image={getDscrClusterHero(URL)}
      />
      <RankKit page="dscr-second-lien-heloc" part="answer" />
      <PageEssentials hideAnswer hideTldr hideToc />
      <Breadcrumbs
        items={[
          { name: "Home", href: "/" },
          { name: "DSCR Loans", href: "/dscr-loans" },
          { name: "Investment Property HELOC", href: "/dscr-heloc" },
          { name: "Second-Lien HELOC" },
        ]}
      />

      <TableOfContents
        items={[
          { href: "#tldr", label: "TL;DR" },
          { href: "#what-it-is", label: "What it is" },
          { href: "#pricing", label: "Price a 2nd lien HELOC" },
          { href: "#cltv", label: "How CLTV works" },
          { href: "#first-vs-second", label: "1st lien vs. 2nd lien" },
          { href: "#who-it-fits", label: "Who it fits" },
          { href: "#pricing-factors", label: "Terms & pricing factors" },
          { href: "#faq", label: "FAQ" },
        ]}
      />

      <TLDR
        id="tldr"
        points={[
          "This is a second-position HELOC on investment property — not a closed-end second mortgage and not an owner-occupied line.",
          "The existing first mortgage stays in place where program rules and the first lienholder's terms permit.",
          "Combined loan-to-value is (existing first-lien balance + requested line amount) ÷ property value.",
          "Note rate and origination fee are shown separately; the fee is never folded into the rate.",
          "Business-purpose, non-owner-occupied use only. Availability varies by state and is validated before pricing.",
          "Figures are illustrative, subject to change, and are not an approval, commitment, or guarantee of financing.",
        ]}
      />

      <Section id="what-it-is">
        <SectionHeader
          eyebrow="Second position, explained"
          title="What a second-lien investment property HELOC actually is"
          intro="Lien position is the order in which recorded loans are repaid if the property is sold or foreclosed. A second-lien HELOC is recorded after your existing mortgage, so that first loan keeps its priority — and its rate and terms."
        />
        <div className="mt-6 max-w-3xl space-y-4 text-muted-foreground">
          <p>
            Investors reach for a second-position line when the first mortgage on a rental is worth keeping. Refinancing
            the whole balance to access equity resets the rate on every dollar you already borrowed. A 2nd lien HELOC
            leaves that structure alone and adds a separate, smaller obligation behind it, secured by the same property.
          </p>
          <p>
            Because the line sits behind an existing loan, the underwriting question is not just how much the property
            is worth — it is how much total debt the property will carry once the new line is added. That is why
            combined loan-to-value, rather than plain loan-to-value, drives eligibility and pricing on this product.
          </p>
          <p>
            This is a business-purpose program on non-owner-occupied property. It is not available for a primary
            residence, and proceeds may not be used for personal, family, or household purposes. Simply Approved
            Mortgages LLC is a mortgage broker, not the funding lender.
          </p>
        </div>
        <div className="mt-8">
          <StatRow
            items={[
              { label: "Lien position", value: "Second" },
              { label: "Occupancy", value: "Non-owner-occupied" },
              { label: "Leverage measure", value: "Combined LTV" },
              { label: "Purpose", value: "Business only" },
            ]}
          />
        </div>
      </Section>

      <HelocPricingTool
        id="pricing"
        surface="dscr-second-lien-heloc-page"
        defaultLienPosition={2}
        title="Price a second-lien investment property HELOC"
        intro="Second lien is preselected. Enter the property value, your existing first-lien balance, the amount you want to draw, FICO, term, and property state. Pricing comes from the current wholesale pricing schedule, is subject to change, and is not an approval, commitment, or guarantee of financing."
      />

      <Section id="cltv">
        <SectionHeader
          eyebrow="The math"
          title="How combined loan-to-value is calculated on a 2nd lien"
          intro="The pricing tool above uses one formula, and it is the same formula underwriting uses to place your scenario in a CLTV band."
        />
        <div className="mt-6 max-w-3xl space-y-4 text-muted-foreground">
          <p className="rounded-xl border border-border bg-muted/40 p-4 font-medium text-foreground">
            CLTV = (existing first-lien balance + requested HELOC amount) ÷ property value
          </p>
          <p>
            <strong className="text-foreground">Illustrative example — not a quote and not a rate offer.</strong> A
            rental is valued at $500,000 and carries a $250,000 first mortgage. You request a $100,000 second-position
            line. Total debt against the property becomes $350,000, so CLTV is $350,000 ÷ $500,000 = 70.00%. That 70%
            figure, together with your FICO band, lien position, term, and the origination fee option, is what the
            pricing engine matches against the schedule. No rate is implied by this example; run your own scenario above
            to see what the current schedule returns.
          </p>
          <p>
            Two things follow from the formula. First, a larger existing first-lien balance reduces how much line the
            same property can support. Second, a lower requested draw can move a scenario into a lower CLTV band, which
            is often where more options appear. Both are worth testing in the tool before you commit to a number.
          </p>
        </div>
      </Section>

      <Section id="first-vs-second">
        <SectionHeader
          eyebrow="Know the difference"
          title="First-lien equity loan vs. second-lien HELOC"
          intro="Both draw on the same equity. Which one applies depends entirely on whether a mortgage is already recorded against the property."
        />
        <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full min-w-[620px] text-sm">
            <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Factor</th>
                <th className="px-4 py-3">Non-primary 1st lien</th>
                <th className="px-4 py-3">Non-primary 2nd lien HELOC</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                ["Existing mortgage", "None, or paid off at closing", "Stays in place where rules permit"],
                ["Leverage measure", "Loan amount ÷ property value", "(First-lien balance + line) ÷ value"],
                ["Repayment priority", "First", "Behind the existing first lien"],
                ["Typical use", "Free-and-clear rental equity access", "Keep a below-market first-lien rate"],
                ["Priced by", "FICO band, CLTV band, term, fee option", "FICO band, CLTV band, term, fee option"],
              ].map((row) => (
                <tr key={row[0]}>
                  <td className="px-4 py-3 font-semibold text-foreground">{row[0]}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row[1]}</td>
                  <td className="px-4 py-3 text-brand">{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          Both lien positions can be priced in the tool above — switch the lien position field to compare. For the
          broader product overview, see the{" "}
          <a className="font-semibold text-brand underline underline-offset-4" href="/dscr-heloc">
            investment property HELOC page
          </a>
          , or open the{" "}
          <a className="font-semibold text-brand underline underline-offset-4" href="/dscr-heloc-rates">
            HELOC pricing tool
          </a>
          .
        </p>
      </Section>

      <Section id="who-it-fits">
        <SectionHeader eyebrow="Fit check" title="Who a second-position line tends to suit" />
        <div className="mt-8">
          <FeatureGrid
            items={[
              {
                title: "Low-rate first mortgage holders",
                body: "You financed the rental when rates were lower and refinancing the whole balance to reach equity would cost more than the equity is worth.",
              },
              {
                title: "Investors with equity but no urgency",
                body: "You want access standing by for the next acquisition or a rehab budget rather than a lump sum sitting idle.",
              },
              {
                title: "Portfolio owners managing leverage",
                body: "You are adding measured debt to one property while keeping total combined leverage inside a band you are comfortable carrying.",
              },
              {
                title: "Business-purpose borrowers",
                body: "The property is a rental or other investment asset and the proceeds are for business use — never personal, family, or household purposes.",
              },
            ]}
          />
        </div>
      </Section>

      <ProsCons
        title="Second-lien HELOC: benefits and trade-offs"
        pros={[
          "Existing first mortgage and its rate stay in place",
          "Access equity without refinancing the full balance",
          "Note rate and origination fee are quoted separately and transparently",
          "Multiple term and fee options priced from one scenario",
        ]}
        cons={[
          "Combined leverage limits are tighter than first-lien pricing",
          "Requires an accurate existing first-lien balance to price",
          "Subordination or first lienholder consent can be required",
          "Not offered in every state and not available for owner-occupied property",
        ]}
      />

      <Section id="pricing-factors">
        <SectionHeader
          eyebrow="What moves the number"
          title="Terms and pricing factors on a 2nd lien HELOC"
          intro="Five inputs determine which cells of the pricing schedule your scenario matches. There is no interpolation between bands — a scenario either matches an eligible cell or it returns no pricing."
        />
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {[
            ["Lien position", "Second position prices differently from a non-primary first lien on the same property."],
            ["Combined LTV band", "Driven by the formula above. Bands are discrete, so small changes to the draw can change the band."],
            ["FICO band", "Credit tier is banded, not continuous. A score at a band edge is priced on the band it lands in."],
            ["Term", "10, 15, 20, or 30 years. Term changes both the matched pricing cell and the estimated payment."],
            ["Origination fee option", "Separate upfront fee choices are quoted alongside each note rate — the fee is never added into the rate."],
            ["Property state", "Required. Only states where this program is currently offered can be priced; excluded states return no pricing."],
          ].map(([t, b]) => (
            <div key={t} className="rounded-xl border border-border bg-card p-4">
              <div className="font-semibold text-foreground">{t}</div>
              <p className="mt-1 text-sm text-muted-foreground">{b}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 max-w-3xl text-sm text-muted-foreground">
          Estimated principal and interest excludes taxes, insurance, HOA dues, and other property costs, so your actual
          payment may be higher. Rates and terms are subject to change and may not be available at commitment or
          closing. Nothing shown here is an approval, commitment, or offer of credit.
        </p>
      </Section>

      <InlineCTA
        variant="soft"
        title="See what a second-position line could look like on your rental"
        body="Send the property address, estimated value, and current first-lien balance for a scenario review."
        href="/apply?product=heloc"
      />

      <RankKit page="dscr-second-lien-heloc" part="details" />
      <Section id="faq">
        <SectionHeader eyebrow="FAQ" title="DSCR second-lien HELOC questions" />
        <div className="mt-8">
          <FAQ items={FAQS} />
        </div>
        <p className="mt-8 text-xs text-muted-foreground">
          Business-purpose, non-owner-occupied investment property financing only; proceeds may not be used for
          personal, family, or household purposes. Simply Approved Mortgages LLC (NMLS #2620881) is a mortgage broker
          and is not the funding lender. Figures are illustrative, subject to change without notice, and are not a
          quote, offer, approval, or commitment to lend. Program and state availability vary. Equal Housing
          Opportunity.
        </p>
      </Section>

      <DscrProgramLinks currentPath={URL} />
      <DscrCrossProductLinks currentPath={URL} />

      <ExpertInsight insightKey="dscr-heloc" />

      <CTABand
        title="Price and submit a second-lien HELOC scenario"
        body="Same-business-day scenario review on complete submissions."
        href="/apply?product=heloc"
      />
    <PageOutro hideInsight />
      </SiteShell>
  );
}
